import type { Raster } from '@repo/warp-engine';
import { useEffect, useRef, useState } from 'react';
import { loadImageRaster } from '@/shared/lib';
import { encodePreviewImage } from './encode-preview-image';
import { type UseFacePreviewInput, useFacePreview } from './use-face-preview';

/** 상호작용이 멈춘 뒤 앞면을 다시 그리기까지 기다리는 시간(ms) — 연속 편집 중에는 그리지 않는다. */
export const DRAFT_PREVIEW_DELAY_MS = 150;

/** 요청 화면에 작게(긴 변 640px) 보여 줄 그림이라 사진·마스크·이미지를 이만큼만 읽어 렌더를 가볍게 둔다. */
export const DRAFT_PREVIEW_MAX_PIXELS = 640 * 640;

const loadSmallRaster = (url: string) =>
  loadImageRaster(url, { maxPixels: DRAFT_PREVIEW_MAX_PIXELS });

type DraftPreviewInput = Omit<
  UseFacePreviewInput,
  'mode' | 'faceKey' | 'onSettled' | 'onFailed'
> & {
  /** 비회원 편집에서만 켠다(회원은 디자인을 저장해 요청한다). */
  readonly enabled: boolean;
};

export interface DraftPreviewSnapshot {
  /**
   * 문서가 `revision` 일 때 그려 둔 앞면 미리보기를 webp data URL 로 돌려준다(G12).
   * 아직 그리지 못했거나 다른 revision 의 그림이거나 만들 수 없으면 null — 기다리지 않는다.
   */
  readonly capture: (revision: number) => string | null;
}

/**
 * 비회원 [견적 요청하기]에 쓸 앞면 미리보기 그림을 미리 그려 둔다. 면 미리보기 파이프라인(useFacePreview)을 앞면에 한 번 돌려
 * 최신 결과를 래스터로 쥐고 있다가, 요청 순간 capture 가 같은 revision 일 때만 data URL 로 바꿔 준다.
 * 현재 보는 면·평면/곡면과 상관없이 앞면을 그리고, 요청을 기다리게 하지 않는다 — 못 그렸으면 그림 없이 간다.
 */
export const useDraftPreviewSnapshot = ({
  enabled,
  ...input
}: DraftPreviewInput): DraftPreviewSnapshot => {
  const [target, setTarget] = useState<number | null>(null);
  const [settledRevision, setSettledRevision] = useState<number | null>(null);
  const [failedRevision, setFailedRevision] = useState<number | null>(null);
  const settled = useRef<{ readonly revision: number; readonly raster: Raster } | null>(null);
  const ready =
    enabled &&
    input.definition?.faces.front !== undefined &&
    input.definition.colors.some(
      (entry) => entry.colorOptionId === input.colorOptionId && entry.faces.front !== undefined,
    );
  const key = `${input.revision}:${input.colorOptionId}`;

  // biome-ignore lint/correctness/useExhaustiveDependencies: 문서·색상 키가 바뀔 때만 다시 예약한다.
  useEffect(() => {
    if (!ready) return;
    const timer = setTimeout(() => setTarget(input.revision), DRAFT_PREVIEW_DELAY_MS);
    return () => clearTimeout(timer);
  }, [ready, key]);

  const wanted =
    ready &&
    target === input.revision &&
    settledRevision !== input.revision &&
    failedRevision !== input.revision;
  useFacePreview({
    ...input,
    faceKey: 'front',
    mode: wanted ? 'rendering' : 'flat',
    deps: { loadRaster: loadSmallRaster, ...input.deps },
    onSettled: (revision, result) => {
      settled.current = { revision, raster: result.raster };
      setSettledRevision(revision);
    },
    onFailed: (revision) => setFailedRevision(revision),
  });

  return {
    capture: (revision) => {
      const snapshot = settled.current;
      return snapshot !== null && snapshot.revision === revision
        ? encodePreviewImage(snapshot.raster)
        : null;
    },
  };
};
